'use client';

import { AppImageClient } from '@components/appImage';
import { JSX } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { CardVariant, Slide } from '../types';

import { TranslatedComponent } from '@/shared/types';

interface ServiceAdvantagesSectionProps extends TranslatedComponent {
  slides?: Slide[];
}

/** Блок с описанием преимуществ сервиса */
export const ServiceAdvantagesSection = ({
  slides,
  translations,
}: ServiceAdvantagesSectionProps) => {
  return (
    <section className="m-auto w-full max-w-[1208px] px-4 pt-16 max-lg:max-w-full md:pt-30">
      <h3 className="min-dm:tracking-[-0,44px] pb-9 text-[30px] leading-[52px] font-medium tracking-[-0.3px] max-sm:leading-[38px] md:pb-[48px] md:text-[44px]">
        {translations.sectionServiceAdvantagesTitle}
      </h3>

      <>
        {/** Блок для мобильных устройств */}
        <div className="lg:hidden">
          <Swiper
            spaceBetween={16}
            slidesPerView="auto"
            className="ml-[-8px]! pr-14! pl-6!"
          >
            {slides?.map((slide) => (
              <SwiperSlide key={slide.id}>
                <div className="bg-surface-base-secondary relative h-[240px] overflow-hidden rounded-[20px]">
                  {renderCard(slide, 'sm')}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/** Блок для браузеров */}
        <div className="grid grid-cols-3 gap-8 pb-3 max-lg:hidden">
          {slides?.map((slide) => (
            <div
              key={slide.id}
              className="bg-surface-base-secondary relative size-full h-[289px] overflow-hidden rounded-[20px]"
            >
              {renderCard(slide, 'lg')}
            </div>
          ))}
        </div>
      </>
    </section>
  );
};

interface ServiceAdvantagesSummaryCardProps extends Pick<Slide, 'imageData'> {
  title: string;
}

const ServiceAdvantagesSummaryCard = ({
  title,
  imageData,
}: ServiceAdvantagesSummaryCardProps) => {
  return (
    <div className="size-full bg-(--surface-base-inverse-primary)">
      <h4 className="mas-xm:p-5 p-6 text-[24px] leading-[28px] font-medium tracking-[-0.24px]">
        {title}
      </h4>
      {imageData?.sm && (
        <AppImageClient
          {...imageData.sm}
          alt="Background image"
          className="absolute right-0 bottom-0 w-full md:hidden"
        />
      )}
      {imageData?.lg && (
        <AppImageClient
          {...imageData.lg}
          alt="Background image"
          className="absolute right-0 bottom-0 w-full max-sm:hidden"
        />
      )}
    </div>
  );
};

const renderCard = (slide: Slide, deafaultVariant: CardVariant) => {
  const CARD_MAP: Record<CardVariant, (slide: Slide) => JSX.Element> = {
    summary: (slide: Slide) => (
      <ServiceAdvantagesSummaryCard
        title={slide.titleKey}
        imageData={slide.imageData}
      />
    ),
    sm: (slide: Slide) => {
      return (
        <div className="flex size-full h-[240px] flex-col justify-between p-5">
          <div>
            <h4 className="pb-2 text-[20px] leading-[24px] font-medium tracking-[-0.1px]">
              {slide.titleKey}
            </h4>
            <span className="text-[16px] leading-[24px] tracking-[-0.16px]">
              {slide.descriptionKey}
            </span>
          </div>
          {slide.icon}
        </div>
      );
    },
    lg: (slide: Slide) => {
      return (
        <div className="flex size-full flex-col justify-between p-6">
          <div>
            <h4 className="pb-2 text-[24px] leading-[32px] font-medium tracking-[-0.12px]">
              {slide.titleKey}
            </h4>
            <span className="text-[16px] leading-[24px] tracking-[-0.16px]">
              {slide.descriptionKey}
            </span>
          </div>
          {slide.icon}
        </div>
      );
    },
  };

  return CARD_MAP[slide.variant ?? deafaultVariant](slide);
};
